import { IEventInfoBlockParams } from "Bands/types";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { APIService } from "shared/services";
import {
  IAPIError,
  IDataKeys,
  IInfoBlockItem,
  IInfoBlockItemType,
  IJSONAPIItem,
} from "shared/types";

interface IJSONAPIInfoBlock {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIInfoBlockCollection {
  [IDataKeys.eventInfoBlock]: IJSONAPIInfoBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class EventInfoBlockService {
  static mapItemsToIds = (items: IInfoBlockItemType[], type: IInfoBlockItem) =>
    items.map(item => API_ENDPOINTS.INFO_BLOCK_ITEM(item.id, type));

  static getBlocks = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIInfoBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const blocks: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_INFO_BLOCKS,
          data: queryParams,
        });
        resolve(blocks as IJSONAPIInfoBlockCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_INFO_BLOCKS);
        return reject(error);
      }
    });

  static getBlock = (id: string): Promise<IJSONAPIInfoBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT_INFO_BLOCK(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_INFO_BLOCK);
        return reject(error);
      }
    });

  static createBlock = (data: IEventInfoBlockParams): Promise<IJSONAPIInfoBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_INFO_BLOCKS,
          data: {
            ...data,
            contacts: EventInfoBlockService.mapItemsToIds(
              data?.contacts || [],
              IInfoBlockItem.contact
            ),
            places: EventInfoBlockService.mapItemsToIds(data?.places || [], IInfoBlockItem.place),
            files: EventInfoBlockService.mapItemsToIds(data?.files || [], IInfoBlockItem.file),
            images: EventInfoBlockService.mapItemsToIds(data?.images || [], IInfoBlockItem.image),
          },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_INFO_BLOCK);
        return reject(error);
      }
    });

  static editBlock = ({
    id,
    data,
  }: {
    id: string;
    data: IEventInfoBlockParams;
  }): Promise<IJSONAPIInfoBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_INFO_BLOCK(id),
          data: {
            ...data,
            contacts: EventInfoBlockService.mapItemsToIds(
              data?.contacts || [],
              IInfoBlockItem.contact
            ),
            places: EventInfoBlockService.mapItemsToIds(data?.places || [], IInfoBlockItem.place),
            files: EventInfoBlockService.mapItemsToIds(data?.files || [], IInfoBlockItem.file),
            images: EventInfoBlockService.mapItemsToIds(data?.images || [], IInfoBlockItem.image),
          },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_INFO_BLOCK);
        return reject(error);
      }
    });

  static deleteBlock = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_INFO_BLOCK(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_INFO_BLOCK);
        return reject(error);
      }
    });
}
